import { DeleteOutlined } from "@ant-design/icons";
import { Button, Flex, Form, Select, Space } from "antd";
import React, { useEffect } from "react";
import { ZCopyByValueRule } from "src/common/attrEdit/ZCopyByValueRule";
import { ZUid } from "src/types/zUid";
import { ZAvaliableCopyAttrData } from "../CopyByValueEdit.types";
import { CopyByValueSettingsItem } from "./CopyByValueSettingsItem";

export const copyByValueFieldNames = {
  linkAttributeId: "linkAttributeId",
  parentAttributeId: "parentAttributeId",
} satisfies Partial<Record<keyof ZCopyByValueRule, string>>;

interface DataSrc {
  avaliableCopyAttrData: ZAvaliableCopyAttrData[];
  typeDependentSettings: CopyByValueSettingsItem[];
}

interface PropsCopyByValueForm {
  dataSrc: DataSrc;
  initialValues?: ZCopyByValueRule;
  onSubmit?(data: unknown): void;
  onCancel?(): void;
  onDelete?(): void;
}

export const CopyByValueForm: React.FC<PropsCopyByValueForm> = ({
  dataSrc,
  initialValues,
  onSubmit,
  onCancel,
  onDelete,
}) => {
  const [form] = Form.useForm();
  const curLinkAttrId: ZUid | undefined = Form.useWatch(
    copyByValueFieldNames.linkAttributeId,
    form,
  );
  const curCopySrcAttrId: ZUid | undefined = Form.useWatch(
    copyByValueFieldNames.parentAttributeId,
    form,
  );
  const { avaliableCopyAttrData, typeDependentSettings } = dataSrc;
  const avaliableLinkAttrsMap = new Map(
    avaliableCopyAttrData.map((e) => [e.linkAttributeId, e.attributes]),
  );
  const copySrcAttributes = avaliableLinkAttrsMap.get(curLinkAttrId || "");
  const copySrcAttributesOptions = copySrcAttributes?.map((e) => ({
    label: e.name,
    value: e.id,
  }));
  const linkAttributesOptions = avaliableCopyAttrData.map((e) => ({
    label: e.linkAttributeName,
    value: e.linkAttributeId,
  }));

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, []);

  const onValuesChange = (data: Record<string, unknown>) => {
    if (copyByValueFieldNames.linkAttributeId in data) {
      form.resetFields([copyByValueFieldNames.parentAttributeId]);
    }
    typeDependentSettings.forEach((e) => e.onFormChange(data, form));
  };

  return (
    <Form
      name="CopyByValueEdit"
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      onValuesChange={onValuesChange}
    >
      <Flex vertical gap={16}>
        <Form.Item
          name={copyByValueFieldNames.linkAttributeId}
          label="Связанная ссылка на другой объект"
          rules={[{ required: true }]}
        >
          <Select
            options={linkAttributesOptions}
            allowClear
            placeholder="Выберите ссылку"
          />
        </Form.Item>
        <Form.Item
          name={copyByValueFieldNames.parentAttributeId}
          label="Атрибут-источник"
          rules={[{ required: true }]}
        >
          <Select
            options={copySrcAttributesOptions}
            disabled={!curLinkAttrId}
            allowClear
            placeholder={
              !curLinkAttrId
                ? "Сначала необходимо выбрать ссылку на другой объект"
                : "Выберите атрибут"
            }
          />
        </Form.Item>

        {typeDependentSettings.map((e) => (
          <React.Fragment key={e.key}>
            {e.render({
              avaliableCopyAttrData,
              curCopySrcAttrId,
              curLinkAttrId,
            })}
          </React.Fragment>
        ))}

        <Flex gap={16} justify="space-between">
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={onDelete}
            title="Все поля будут очищены и окно закроется"
          >
            Удалить правило
          </Button>
          <Space>
            <Button type="primary" htmlType="submit">
              Применить
            </Button>
            <Button onClick={onCancel}>Отмена</Button>
          </Space>
        </Flex>
      </Flex>
    </Form>
  );
};
